/**
 * Popover - Core Component (Passthrough Wrapper)
 *
 * This is a thin wrapper around shadcn/ui Popover components.
 * It provides a consistent import path and allows for future project-specific
 * defaults or modifications without changing consumer code.
 *
 * Purpose:
 * - Consistent import path: always import from '@/app/_components/core'
 * - Type re-exports for convenience
 * - Default sideOffset of 4 for consistent spacing
 * - Future-proof: modifications can be added here without breaking changes
 *
 * This component does NOT add custom styling - it passes through
 * all props directly to the underlying shadcn/ui components.
 *
 * @see {@link file://./../ui/popover.tsx} for the underlying implementation
 */
'use client';

import { forwardRef, type ComponentRef, type ReactNode } from 'react';
import {
  Popover as ShadcnPopover,
  PopoverTrigger as ShadcnPopoverTrigger,
  PopoverContent as ShadcnPopoverContent,
  PopoverAnchor as ShadcnPopoverAnchor,
} from '@/app/_components/ui/popover';
import { cn } from '@/app/_lib/utils';

export interface PopoverProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  children?: ReactNode;
}

export interface PopoverTriggerProps {
  asChild?: boolean;
  children?: ReactNode;
  className?: string;
}

export interface PopoverContentProps {
  className?: string;
  sideOffset?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  children?: ReactNode;
}

export interface PopoverAnchorProps {
  asChild?: boolean;
  children?: ReactNode;
  className?: string;
}

/**
 * Popover root component - wrapper over shadcn/ui Popover
 *
 * @example
 * ```tsx
 * <Popover>
 *   <PopoverTrigger asChild>
 *     <Button variant="outline">Open popover</Button>
 *   </PopoverTrigger>
 *   <PopoverContent>
 *     <p>Place content for the popover here.</p>
 *   </PopoverContent>
 * </Popover>
 * ```
 */
export const Popover = ({ ...props }: PopoverProps) => {
  return <ShadcnPopover {...props} />;
};

Popover.displayName = 'Popover';

/**
 * PopoverTrigger - Element that triggers the popover
 */
export const PopoverTrigger = forwardRef<
  ComponentRef<typeof ShadcnPopoverTrigger>,
  PopoverTriggerProps
>(({ className, ...props }, ref) => {
  return (
    <ShadcnPopoverTrigger ref={ref} className={cn(className)} {...props} />
  );
});

PopoverTrigger.displayName = 'PopoverTrigger';

/**
 * PopoverContent - The content area of the popover
 */
export const PopoverContent = forwardRef<
  ComponentRef<typeof ShadcnPopoverContent>,
  PopoverContentProps
>(({ className, sideOffset = 4, ...props }, ref) => {
  return (
    <ShadcnPopoverContent
      ref={ref}
      sideOffset={sideOffset}
      className={cn('rounded-popover-radius', className)}
      {...props}
    />
  );
});

PopoverContent.displayName = 'PopoverContent';

/**
 * PopoverAnchor - Optional anchor element for positioning
 */
export const PopoverAnchor = forwardRef<
  ComponentRef<typeof ShadcnPopoverAnchor>,
  PopoverAnchorProps
>(({ className, ...props }, ref) => {
  return <ShadcnPopoverAnchor ref={ref} className={cn(className)} {...props} />;
});

PopoverAnchor.displayName = 'PopoverAnchor';

export default Popover;
